import React, { createContext, useContext, useState } from "react";
import PropTypes from "prop-types";
import { isDesktop, isLargeDesktop } from "../utils/device-helper";
import { useCallback } from "react";
import { isEqual } from "../utils/fp/isEqual";

/**
 * @typedef {object} StickyPlayerContextValue
 * @property {boolean} init True once a `StickyPlayer` has registered with the provider.
 * @property {string | null} openId Id of the audio open in the sticky player.
 * @property {string | null} playingId Id of the audio currently playing.
 * @property {boolean} isLoaded True once the open player's audio has loaded.
 * @property {boolean} isExpandable
 * @property {boolean} isExpanded
 * @property {object} [human] Human-read details for the open audio, set by `StickyPlayer`.
 * @property {string} [ledeArtUrl] Lede art for the open audio, set by `StickyPlayer`.
 * @property {() => void} handleClick Toggles play/pause on the open player.
 * @property {(update: Partial<Omit<StickyPlayerContextValue, "updateContext">>) => void} updateContext
 *   Merges `update` into the context.
 */

/** @type {React.Context<StickyPlayerContextValue>} */
const StickyPlayerContext = createContext({
  init: false,
  openId: null,
  isLoaded: false,
  playingId: null,
  isExpandable: true,
  isExpanded: false,
  handleClick: () => {},
  updateContext: () => {}
});

/** @returns {StickyPlayerContextValue} */
export const useStickyPlayerContext = () => useContext(StickyPlayerContext);

/** @param {{ children?: React.ReactNode }} props */
export const StickyPlayerProvider = ({ children }) => {
  let isExpanded = false;

  if ((typeof window !== "undefined" && isDesktop()) || isLargeDesktop()) {
    isExpanded = true;
    localStorage.getItem("userExpandPreference", isExpanded);
  }

  const [value, setValue] = useState({
    init: false,
    openId: null,
    isLoaded: false,
    playingId: null,
    isExpandable: true,
    isExpanded: isExpanded,
    handleClick: () => {}
  });
  const updateContext = useCallback(update => {
    setValue(current => {
      const newState = { ...current, ...update };
      return isEqual(newState, current) ? current : newState;
    });
  }, []);

  return (
    <StickyPlayerContext.Provider value={{ ...value, updateContext }}>
      {children}
    </StickyPlayerContext.Provider>
  );
};

StickyPlayerProvider.propTypes = {
  children: PropTypes.node
};
